// BankAuction.js
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh",
  marginBottom: "80px"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#654321',
  },
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  margin: '4px 0',
  backgroundColor: '#f8f9fa',
  borderRadius: '6px',
  border: '1px solid #e9ecef',
}));

const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
}));

const BankAuction = () => {
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    auctionId: '',
    bankName: '',
    propertyType: '',
    actionType: '',
    location: '',
    cityTown: '',
    areaTown: '',
    area: '',
    possession: '',
    reservePrice: '',
    possessionStatus: '',
    emdAmount: '',
    bidIncrement: '',
    emdSubmission: '',
    auctionStartDate: '',
    auctionEndDate: '',
    bankContact: '',
    downloadAuctionFile: '', // This should be a short string (file path/name)
    description: '' // Added description field
  });

  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    console.log("📌 Saving bank auction data...");

    try {
      // Prepare the data object according to Django API structure
      const requestData = {
        auction_id: formData.auctionId || '',
        bank_name: formData.bankName || '',
        property_type: formData.propertyType || '',
        action_type: formData.actionType || '',
        location: formData.location || '',
        city_town: formData.cityTown || '',
        area_town: formData.areaTown || '',
        area: parseFloat(formData.area) || 0,
        units: '', // Add this field as it's in your API
        possession: formData.possession || '',
        reserve_price: formData.reservePrice || '',
        possession_status: formData.possessionStatus || '',
        emd_amount: parseFloat(formData.emdAmount?.replace(/,/g, '')) || 0,
        bid_increment: parseFloat(formData.bidIncrement?.replace(/,/g, '')) || 0,
        emd_submission: formData.emdSubmission || '',
        auction_start_datetime: formData.auctionStartDate || '',
        auction_end_datetime: formData.auctionEndDate || '',
        bank_contact_details: formData.bankContact || '',
        download_auction_file: formData.downloadAuctionFile || '', // Short string (file name/path)
        description: formData.description || '', // Added description field
        user_id: userId || ''
      };

      console.log("📤 Data being sent:", requestData);

      // First, save the main auction data
      const response = await fetch("https://landnest.net:81/auction-property/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Bank auction data saved successfully:", result);
        
        // If files were selected and we have the auction ID, upload files separately
        if (selectedFiles.length > 0 && result.bankprop_id) {
          await uploadFiles(result.bankprop_id, selectedFiles);
        }
        
        navigate(-1);
      } else {
        const errorText = await response.text();
        console.log("❌ Failed to save bank auction data:", errorText);
        alert("Failed to save data: " + errorText);
      }
    } catch (error) {
      console.log("🚨 Error saving bank auction data:", error);
      alert("Error saving data: " + error.message);
    }
  };

  // Separate function to handle file uploads
  const uploadFiles = async (bankpropId, files) => {
    try {
      const formData = new FormData();
      formData.append('bankprop_id', bankpropId);
      
      files.forEach((file, index) => {
        formData.append('auction_files', file);
      });

      const uploadResponse = await fetch("https://landnest.net:81/upload-auction-files/", {
        method: "POST",
        body: formData,
      });

      if (uploadResponse.ok) {
        console.log("✅ Files uploaded successfully");
      } else {
        console.log("❌ File upload failed");
      }
    } catch (error) {
      console.log("🚨 Error uploading files:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBackClick = () => navigate(-1);

  // Handle file selection - store file names in downloadAuctionFile field
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // For download_auction_file field: use first file name or generate a short identifier
      const firstFileName = files[0].name;
      const shortFileName = firstFileName.length > 50 
        ? firstFileName.substring(0, 47) + '...' 
        : firstFileName;
      
      // Store the short file name in download_auction_file field
      handleFormDataChange('downloadAuctionFile', shortFileName);
      
      // Store actual files for separate upload
      setSelectedFiles(prev => [...prev, ...files]);
    }
    
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle file deletion
  const handleFileDelete = (indexToDelete) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToDelete));
    
    // If we're deleting the first file, update the downloadAuctionFile field
    if (indexToDelete === 0 && selectedFiles.length > 0) {
      if (selectedFiles.length === 1) {
        // No files left, clear the field
        handleFormDataChange('downloadAuctionFile', '');
      } else {
        // Update with the new first file name
        const newFirstFileName = selectedFiles[1].name;
        const shortFileName = newFirstFileName.length > 50 
          ? newFirstFileName.substring(0, 47) + '...' 
          : newFirstFileName;
        handleFormDataChange('downloadAuctionFile', shortFileName);
      }
    }
  };

  // Clear all files
  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    handleFormDataChange('downloadAuctionFile', '');
  };

  // Options for dropdowns
  const bankOptions = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India'
  ];

  const propertyTypeOptions = [
    'Plots',
    'Residential Building',
    'Commercial Building',
    'Land',
    'Flat',
    'House',
    'Villa'
  ];

  const possessionOptions = [
    'Physical',
    'Symbolic',
    'Physical/Symbolic'
  ];

  const cityOptions = [
    'Bangalore',
    'Hyderabad',
    'Delhi',
    'Mumbai',
    'Chennai',
    'Pune',
    'Kolkata'
  ];

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />

      <Box sx={{ 
        padding: '16px', 
        backgroundColor: '#f5f1eb',
        minHeight: '100vh',
        height: '100%',
        marginTop: "15%",
        paddingBottom: "80px"
      }}>
        <FilterContainer>
          {/* Basic Information */}
          <SectionTitle>Basic Information</SectionTitle>
          <TextField
            fullWidth
            label="Auction ID"
            placeholder="e.g., 12534"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.auctionId}
            onChange={(e) => handleFormDataChange('auctionId', e.target.value)}
          />

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Bank Name</InputLabel>
            <Select
              value={formData.bankName}
              label="Bank Name"
              onChange={(e) => handleFormDataChange('bankName', e.target.value)}
            >
              {bankOptions.map((bank) => (
                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Property Type</InputLabel>
            <Select
              value={formData.propertyType}
              label="Property Type"
              onChange={(e) => handleFormDataChange('propertyType', e.target.value)}
            >
              {propertyTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Action Type"
            placeholder="Enter action type"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.actionType}
            onChange={(e) => handleFormDataChange('actionType', e.target.value)}
          />

          {/* Location Details */}
          <SectionTitle>Location Details</SectionTitle>
          <TextField
            fullWidth
            label="Location"
            placeholder="e.g., GPS coordinates"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.location}
            onChange={(e) => handleFormDataChange('location', e.target.value)}
          />

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>City/Town</InputLabel>
            <Select
              value={formData.cityTown}
              label="City/Town"
              onChange={(e) => handleFormDataChange('cityTown', e.target.value)}
            >
              {cityOptions.map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Area/Town"
            placeholder="e.g., Yeswanthpura"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.areaTown}
            onChange={(e) => handleFormDataChange('areaTown', e.target.value)}
          />

          <TextField
            fullWidth
            label="Area"
            placeholder="e.g., 900 sft"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.area}
            onChange={(e) => handleFormDataChange('area', e.target.value)}
          />

          {/* Possession Details */}
          <SectionTitle>Possession Details</SectionTitle>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Possession</InputLabel>
            <Select
              value={formData.possession}
              label="Possession"
              onChange={(e) => handleFormDataChange('possession', e.target.value)}
            >
              {possessionOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Possession Status</InputLabel>
            <Select
              value={formData.possessionStatus}
              label="Possession Status"
              onChange={(e) => handleFormDataChange('possessionStatus', e.target.value)}
            >
              <MenuItem value="Physical">Physical</MenuItem>
              <MenuItem value="Symbolic">Symbolic</MenuItem>
              <MenuItem value="Symbolic/Physical">Symbolic/Physical</MenuItem>
            </Select>
          </FormControl>

          {/* Auction Details */}
          <SectionTitle>Auction Details</SectionTitle>
          <TextField
            fullWidth
            label="Reserve Price"
            placeholder="e.g., 31,32,000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.reservePrice}
            onChange={(e) => handleFormDataChange('reservePrice', e.target.value)}
          />

          <TextField
            fullWidth
            label="EMD Amount"
            placeholder="e.g., 3,13,200"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.emdAmount}
            onChange={(e) => handleFormDataChange('emdAmount', e.target.value)}
          />

          <TextField
            fullWidth
            label="Bid Increment"
            placeholder="e.g., 10,000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.bidIncrement}
            onChange={(e) => handleFormDataChange('bidIncrement', e.target.value)}
          />

          <TextField
            fullWidth
            label="EMD Submission Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.emdSubmission}
            onChange={(e) => handleFormDataChange('emdSubmission', e.target.value)}
          />

          <TextField
            fullWidth
            label="Auction Start Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionStartDate}
            onChange={(e) => handleFormDataChange('auctionStartDate', e.target.value)}
          />

          <TextField
            fullWidth
            label="Auction End Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionEndDate}
            onChange={(e) => handleFormDataChange('auctionEndDate', e.target.value)}
          />

          {/* Contact & Files */}
          <SectionTitle>Contact & Documents</SectionTitle>
          <TextField
            fullWidth
            label="Bank Contact Details"
            placeholder="e.g., xxxxxxxx125"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.bankContact}
            onChange={(e) => handleFormDataChange('bankContact', e.target.value)}
          />

          {/* Description Field */}
          <SectionTitle>Description</SectionTitle>
          <TextField
            fullWidth
            label="Description"
            placeholder="Enter property description..."
            variant="outlined"
            size="small"
            multiline
            rows={3}
            sx={{ mb: 2 }}
            value={formData.description}
            onChange={(e) => handleFormDataChange('description', e.target.value)}
          />

          {/* Auction Files */}
          <SectionTitle>Auction Files</SectionTitle>
          <Box sx={{ mb: 2 }}>
            <label
              htmlFor="auction-files"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "2px dashed #8B4513",
                borderRadius: "8px",
                background: "#fffaf0",
                cursor: "pointer",
                marginBottom: "12px",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f5f1eb";
                e.target.style.borderColor = "#654321";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#fffaf0";
                e.target.style.borderColor = "#8B4513";
              }}
            >
              <AttachFileIcon sx={{ color: '#8B4513' }} />
              <Typography sx={{ color: '#8B4513', fontWeight: '500' }}>
                Click to Select Files
              </Typography>
            </label>

            <input
              id="auction-files"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" // Accept images and documents
            />

            {/* SHOW SELECTED FILES WITH DELETE BUTTONS */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#555", fontWeight: '500' }}>
                    Selected Files ({selectedFiles.length})
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={handleClearAllFiles}
                    sx={{ color: '#d32f2f', fontSize: '12px' }}
                  >
                    Clear All
                  </Button>
                </Box>
                
                {selectedFiles.map((file, index) => (
                  <FileItem key={index}>
                    <FileInfo>
                      <AttachFileIcon sx={{ color: '#8B4513', fontSize: '18px' }} />
                      <Typography sx={{ fontSize: "14px", color: "#333", flex: 1 }}>
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#666" }}>
                        {(file.size / 1024).toFixed(2)} KB
                      </Typography>
                    </FileInfo>
                    <IconButton 
                      size="small" 
                      onClick={() => handleFileDelete(index)}
                      sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </FileItem>
                ))}
                
                {formData.downloadAuctionFile && (
                  <Typography variant="caption" sx={{ color: "#666", mt: 1, display: 'block' }}>
                    Primary file reference: {formData.downloadAuctionFile}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 2 }}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={handleCancel}
              sx={{ 
                borderRadius: '8px',
                borderColor: '#8B4513',
                color: '#8B4513',
                '&:hover': {
                  borderColor: '#654321',
                  backgroundColor: 'rgba(139, 69, 19, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <StyledButton fullWidth onClick={handleSave}>
              Save
            </StyledButton>
          </Stack>
        </FilterContainer>
      </Box>
      <FormsBottomNavbar />
    </>
  );
};

export default BankAuction;